/**
 * Promotional Popup Handler
 * Manages the display and behavior of promotional popups with cookie-based frequency control
 */

class PromotionalPopup {
  constructor() {
    this.overlay = document.getElementById('promoPopupOverlay');
    this.modal = document.getElementById('promoPopupModal');
    
    if (!this.overlay || !this.modal) {
      return;
    }

    this.delay = parseInt(this.overlay.dataset.delay) || 10;
    this.frequency = parseInt(this.overlay.dataset.frequency) || 7;
    this.closeOnOverlay = this.overlay.dataset.closeOnOverlay === 'true';
    
    this.cookieName = 'promo_popup_seen';
    this.init();
  }

  init() {
    // Check if popup should be shown
    if (this.shouldShowPopup()) {
      this.schedulePopup();
    }

    // Setup event listeners
    this.setupEventListeners();
  }

  shouldShowPopup() {
    // Check if popup was recently shown
    const lastSeen = this.getCookie(this.cookieName);
    
    if (!lastSeen) {
      return true;
    }

    const daysSinceLastSeen = this.getDaysSince(parseInt(lastSeen));
    return daysSinceLastSeen >= this.frequency;
  }

  schedulePopup() {
    // Show popup after delay
    setTimeout(() => {
      this.showPopup();
    }, this.delay * 1000);
  }

  showPopup() {
    // Add class to body to prevent scrolling
    document.body.classList.add('promo-popup-open');
    
    // Show the overlay
    this.overlay.classList.add('active');
    
    // Set focus trap
    this.setFocusTrap();
    
    // Track that popup was shown
    this.setCookie(this.cookieName, Date.now().toString(), this.frequency);
    
    // Fire custom event for tracking
    this.fireEvent('promo:popup:shown');
  }

  closePopup() {
    // Add closing animation
    this.overlay.classList.add('closing');
    
    // Remove active class after animation
    setTimeout(() => {
      this.overlay.classList.remove('active', 'closing');
      document.body.classList.remove('promo-popup-open');
      
      // Fire custom event for tracking
      this.fireEvent('promo:popup:closed');
    }, 300);
  }

  setupEventListeners() {
    // Close button
    const closeBtn = this.modal.querySelector('.promo-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closePopup();
      });
    }

    // Overlay click (if enabled)
    if (this.closeOnOverlay) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.closePopup();
        }
      });
    }

    // Prevent modal click from closing
    this.modal.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('active')) {
        this.closePopup();
      }
    });

    // Track button clicks
    const button = this.modal.querySelector('.promo-popup__button');
    if (button) {
      button.addEventListener('click', () => {
        this.fireEvent('promo:popup:button_clicked', {
          link: button.href
        });
      });
    }
  }

  setFocusTrap() {
    // Get all focusable elements
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element
    firstElement.focus();

    // Trap focus within modal
    this.modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    });
  }

  // Cookie management
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }
    return null;
  }

  getDaysSince(timestamp) {
    const now = Date.now();
    const diff = now - timestamp;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  // Event firing for analytics
  fireEvent(eventName, data = {}) {
    const event = new CustomEvent(eventName, {
      detail: data,
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(event);

    // Also fire to dataLayer if available (for Google Analytics)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...data
      });
    }
  }
}

// Initialize popup when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PromotionalPopup();
  });
} else {
  new PromotionalPopup();
}

// Make available globally for manual control
window.PromotionalPopup = PromotionalPopup;
